/**
 * Cookie utility functions for managing browser cookies
 * KVKK compliant cookie management
 */

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
}

export type CookieConsentStatus = 'accepted' | 'rejected' | 'custom' | null;

/**
 * Set a cookie with optional expiration days
 */
export const setCookie = (name: string, value: string, days: number = 365): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete a cookie
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Delete all cookies except essential ones
 */
export const deleteNonEssentialCookies = (): void => {
  // List of analytics and tracking cookies to delete
  const analyticsCookies = [
    '_ga',
    '_gid',
    '_gat',
    '_gac_',
    '_fbp',
    '_fbc',
    'fbp',
    'fbc',
    '__utma',
    '__utmb',
    '__utmc',
    '__utmt',
    '__utmz',
    '__utmv',
    'amplitude_',
    'mixpanel',
    'hotjar',
    '_hjid',
    '_hjIncludedInPageviewSample',
    '_hjAbsoluteSessionInProgress',
    '_hjFirstSeen',
    '_hjSessionUser_',
    '_hjSession_',
    '_hjSessionRejected',
    '_hjid',
    'intercom-session',
    'intercom-id',
  ];

  // Get all cookies
  const allCookies = document.cookie.split(';');
  
  allCookies.forEach((cookie) => {
    const cookieName = cookie.split('=')[0].trim();
    
    // Check if it's an analytics cookie
    const isAnalyticsCookie = analyticsCookies.some((analyticsCookie) => {
      return cookieName.startsWith(analyticsCookie) || cookieName === analyticsCookie;
    });

    if (isAnalyticsCookie) {
      deleteCookie(cookieName);
      // Also try to delete with domain variations
      deleteCookie(`${cookieName};domain=${window.location.hostname}`);
      deleteCookie(`${cookieName};domain=.${window.location.hostname}`);
    }
  });
};

/**
 * Get cookie consent status from localStorage
 */
export const getCookieConsentStatus = (): CookieConsentStatus => {
  const consent = localStorage.getItem('cookie_consent');
  return consent as CookieConsentStatus;
};

/**
 * Get cookie preferences from localStorage
 */
export const getCookiePreferences = (): CookiePreferences | null => {
  const preferences = localStorage.getItem('cookie_preferences');
  if (!preferences) return null;
  try {
    return JSON.parse(preferences) as CookiePreferences;
  } catch {
    return null;
  }
};

/**
 * Save cookie consent status
 */
export const saveCookieConsent = (status: CookieConsentStatus, preferences: CookiePreferences): void => {
  localStorage.setItem('cookie_consent', status || '');
  localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
  localStorage.setItem('cookie_consent_date', new Date().toISOString());
};

/**
 * Check if user has given consent
 */
export const hasConsent = (): boolean => {
  const status = getCookieConsentStatus();
  return status === 'accepted' || status === 'custom';
};

/**
 * Check if analytics cookies are allowed
 */
export const isAnalyticsAllowed = (): boolean => {
  const status = getCookieConsentStatus();
  if (status === 'accepted') return true;
  if (status === 'custom') {
    const preferences = getCookiePreferences();
    return preferences?.analytics === true;
  }
  return false;
};

/**
 * Initialize cookies based on user preferences
 */
export const initializeCookies = (preferences: CookiePreferences): void => {
  // Essential cookies are always set (handled by the app itself)
  
  // Handle analytics cookies
  if (preferences.analytics) {
    // Analytics cookies will be set by analytics services
    // This is just a flag to allow them
    setCookie('analytics_enabled', 'true', 365);
  } else {
    // Remove analytics cookies if not allowed
    deleteCookie('analytics_enabled');
    deleteNonEssentialCookies();
  }
};

